import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import SettingsIcon from '@mui/icons-material/Settings'
import GavelIcon from '@mui/icons-material/Gavel'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import API from '../../services/API'
import { GlobalContext } from '../../App'
import StatCard from '../../components/Cards/StatCard'
import PermissionCheck from '../../helpers/PermissionCheck'
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert'
import { GAP_SEVERITY, READINESS_BAND } from './severity'
import GapSeverityChip from './components/GapSeverityChip'
import EvidenceStatusChip from './components/EvidenceStatusChip'
import KeyQuestionGauge from './components/KeyQuestionGauge'
import { getRAGband } from '../governance/riskRegister/getRAGcolor'

const KEY_QUESTIONS = ['Safe', 'Effective', 'Caring', 'Responsive', 'Well-led']

const REGISTRATION_STATUS_LABEL = {
  registered: 'CQC Registered',
  applying: 'Applying for CQC Registration',
  not_applicable: 'Using CQC Centre voluntarily',
}

const Dashboard = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [recomputing, setRecomputing] = useState(false)
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)

  const loadDashboard = () => {
    setLoading(true)
    API.get('/cqc-centre/dashboard')
      .then((res) => {
        if (res.status === 200) setData(res.data)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadDashboard()
  }, [])

  const handleRecompute = () => {
    setRecomputing(true)
    API.post('/cqc-centre/recompute')
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
          setToast?.({ message: 'CQC Centre recalculated' })
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setRecomputing(false))
  }

  if (loading) return <LinearProgress />
  if (!data)
    return (
      <Alert severity="error">Unable to load the CQC Centre right now.</Alert>
    )

  // A key question with zero evidence hasn't been assessed — its score is a
  // vacuous 100, so treat it as "No data" instead. Older API responses
  // without the counts fall back to showing every score.
  const evidenceCounts = data.evidenceCountsByKeyQuestion
  const hasKqData = (kq) =>
    evidenceCounts ? (evidenceCounts[kq] ?? 0) > 0 : true
  const anyAssessed = KEY_QUESTIONS.some(hasKqData)

  const band = READINESS_BAND(data.overallScore)

  return (
    <PermissionCheck roles={['Governance']} showAlert>
      <Stack gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          gap={1}
        >
          <Box>
            <Typography variant="title1">CQC Centre</Typography>
            <Stack direction="row" gap={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                label={REGISTRATION_STATUS_LABEL[data.registrationStatus]}
              />
              <Typography variant="caption" color="text.secondary">
                {data.lastRecomputedAt
                  ? `Last updated ${moment(data.lastRecomputedAt).fromNow()}`
                  : 'Not yet calculated'}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRecompute}
              disabled={recomputing}
            >
              {recomputing ? 'Recalculating…' : 'Recalculate'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/cqc-centre/settings')}
            >
              Settings
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => navigate('/cqc-centre/pack')}
            >
              Inspection Pack
            </Button>
          </Stack>
        </Stack>

        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                label="Overall Readiness"
                value={anyAssessed ? `${data.overallScore}` : 'No data'}
                afterValue={anyAssessed ? '/ 100' : undefined}
                color={anyAssessed ? band.color : 'greycontrast'}
                onClick={() => navigate('/cqc-centre/gaps')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                label="Critical Gaps"
                value={data.gapCounts.critical}
                color={GAP_SEVERITY.Critical.color}
                onClick={() => navigate('/cqc-centre/gaps?severity=Critical')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                label="Important Gaps"
                value={data.gapCounts.important}
                color={GAP_SEVERITY.Important.color}
                onClick={() => navigate('/cqc-centre/gaps?severity=Important')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                label="Advisory Gaps"
                value={data.gapCounts.advisory}
                color={GAP_SEVERITY.Advisory.color}
                onClick={() => navigate('/cqc-centre/gaps?severity=Advisory')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                label="Actions Required"
                value={data.actionsRequiredCount ?? data.openActionCount ?? 0}
                color={
                  (data.actionsRequiredCount ?? data.openActionCount ?? 0) > 0
                    ? '#F28B1F'
                    : '#00964C'
                }
                onClick={() => navigate('/cqc-centre/gaps')}
              />
            </Grid>
          </Grid>
        </Box>

        <Typography variant="title2">By CQC Key Question</Typography>
        <Box>
          <Grid container spacing={2}>
            {KEY_QUESTIONS.map((kq) => {
              const score = hasKqData(kq)
                ? (data.keyQuestionScores?.[kq] ?? null)
                : null
              return (
                <Grid item xs={12} sm={6} md={2.4} key={kq} display="flex">
                  <KeyQuestionGauge
                    label={kq}
                    score={score}
                    bandColor={READINESS_BAND(score ?? 0).color}
                    onClick={() =>
                      navigate(`/cqc-centre/gaps?keyQuestion=${kq}`)
                    }
                  />
                </Grid>
              )
            })}
          </Grid>
        </Box>

        <Paper
          variant="outlined"
          sx={{ p: 2, cursor: 'pointer' }}
          onClick={() => navigate('/cqc-centre/compliance')}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Stack direction="row" gap={1.5} alignItems="center">
              <FactCheckIcon color="anBlue" />
              <Box>
                <Typography variant="title3">Compliance Questions</Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.complianceSummary
                    ? `${data.complianceSummary.answered} of ${data.complianceSummary.total} regulation questions answered` +
                      (data.complianceSummary.overdue > 0
                        ? ` - ${data.complianceSummary.overdue} overdue for annual review`
                        : data.complianceSummary.dueSoon > 0
                          ? ` - ${data.complianceSummary.dueSoon} due for review soon`
                          : '')
                    : 'Answer the CQC regulation-by-regulation self-assessment for your regulated activities.'}
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/cqc-centre/compliance')}
            >
              Open
            </Button>
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: 2, cursor: 'pointer' }}
          onClick={() => navigate('/cqc-centre/registration')}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Stack direction="row" gap={1.5} alignItems="center">
              <GavelIcon color="anBlue" />
              <Box>
                <Typography variant="title3">Registration Centre</Typography>
                <Typography variant="body2" color="text.secondary">
                  Do you need to be CQC-registered for the medical cover you
                  provide? Track your registration status and the 6 Dec 2027
                  events-registration deadline.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/cqc-centre/registration')}
            >
              Open
            </Button>
          </Stack>
        </Paper>

        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} display="flex">
              <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="title3">Assigned Gaps</Typography>
                  <Button
                    size="small"
                    onClick={() => navigate('/cqc-centre/gaps')}
                  >
                    View all gaps
                  </Button>
                </Stack>
                {(data.assignedGaps ?? []).length === 0 ? (
                  <Alert
                    severity={
                      (data.unassignedGapCount ?? 0) > 0 ? 'warning' : 'success'
                    }
                  >
                    {(data.unassignedGapCount ?? 0) > 0
                      ? `${data.unassignedGapCount} open gap${
                          data.unassignedGapCount === 1 ? '' : 's'
                        } with nobody assigned - fix them directly or assign them to a teammate from the Gaps list.`
                      : 'No open gaps and nothing awaiting attention.'}
                  </Alert>
                ) : (
                  <Stack gap={1}>
                    {data.assignedGaps.map((gap) => (
                      <Stack
                        key={gap._id}
                        direction="row"
                        gap={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => navigate('/cqc-centre/gaps')}
                      >
                        <Box>
                          <Typography variant="body2">{gap.message}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {gap.assignedTo
                              ? `${gap.assignedTo.firstName} ${gap.assignedTo.lastName}`
                              : 'Unassigned'}
                            {gap.assignedAt
                              ? ` - since ${moment(gap.assignedAt).format('Do MMM YYYY')}`
                              : ''}
                          </Typography>
                        </Box>
                        <GapSeverityChip severity={gap.severity} />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} display="flex">
              <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="title3">Key Gaps</Typography>
                  <Button
                    size="small"
                    onClick={() => navigate('/cqc-centre/gaps')}
                  >
                    View all
                  </Button>
                </Stack>
                {data.topGaps.length === 0 ? (
                  <Alert severity="success">
                    No open gaps right now - nice work.
                  </Alert>
                ) : (
                  <Stack gap={1}>
                    {data.topGaps.map((gap) => (
                      <Stack
                        key={gap._id}
                        direction="row"
                        gap={1}
                        alignItems="flex-start"
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 1,
                        }}
                      >
                        <GapSeverityChip severity={gap.severity} />
                        <Typography variant="body2">{gap.message}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    <CrisisAlertIcon
                      fontSize="small"
                      sx={{ color: '#1E7F88' }}
                    />
                    <Typography variant="title3">Risk Register</Typography>
                  </Stack>
                  <Button
                    size="small"
                    onClick={() => navigate('/risk-register')}
                  >
                    View register
                  </Button>
                </Stack>
                {(data.keyRisks ?? []).length === 0 ? (
                  <Alert severity="warning">
                    No risks recorded yet. An active, reviewed risk register is
                    key CQC evidence - add your first risk to get started.
                  </Alert>
                ) : (
                  <Stack gap={1}>
                    {data.keyRisks.map((risk) => {
                      const riskBand = getRAGband(risk.currentRating)
                      return (
                        <Stack
                          key={risk._id}
                          direction="row"
                          gap={1}
                          alignItems="center"
                          justifyContent="space-between"
                          onClick={() => navigate(`/risk-register/${risk._id}`)}
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderLeft: `3px solid ${riskBand.color}`,
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease',
                            '&:hover': { backgroundColor: 'anMuted.main' },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap>
                              #{risk.riskID}{' '}
                              {risk.title ||
                                risk.description ||
                                'Untitled Risk'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {risk.owner ?? 'Unassigned'}
                              {risk.lastReview
                                ? ` - reviewed ${moment(risk.lastReview).fromNow()}`
                                : ' - never reviewed'}
                              {risk.cqcQualityStatementKeys?.length
                                ? ` - ${risk.cqcQualityStatementKeys.length} linked statement${risk.cqcQualityStatementKeys.length === 1 ? '' : 's'}`
                                : ''}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={`${risk.currentRating || '—'} ${riskBand.label}`}
                            sx={{
                              borderRadius: 1,
                              fontWeight: 700,
                              color: '#FFFFFF',
                              backgroundColor: riskBand.color,
                              flexShrink: 0,
                            }}
                          />
                        </Stack>
                      )
                    })}
                  </Stack>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="title3">Recent Evidence</Typography>
                  <Button
                    size="small"
                    onClick={() => navigate('/cqc-centre/evidence')}
                  >
                    View all
                  </Button>
                </Stack>
                {data.recentEvidence.length === 0 ? (
                  <Alert severity="info">
                    Your CQC Centre is warming up. As you use Training, DBS,
                    Governance, Fleet and other modules, evidence will start
                    appearing here automatically.
                  </Alert>
                ) : (
                  <Stack gap={1}>
                    {data.recentEvidence.map((item) => (
                      <Stack
                        key={item._id}
                        direction="row"
                        gap={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="body2">{item.title}</Typography>
                          <Stack direction="row" gap={1} alignItems="center">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {moment(item.evidenceDate).format('Do MMM YYYY')}
                            </Typography>
                            {item.keyQuestion && (
                              <Chip
                                label={item.keyQuestion}
                                size="small"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <EvidenceStatusChip status={item.status} />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ mt: 5 }}>
                <Alert severity="info" variant="outlined">
                  This is a self-assessment tool built from your own records. It
                  reflects how complete and current your evidence is against a
                  practical CQC-aligned framework - it is not a CQC rating and
                  does not predict or guarantee any CQC judgement.
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </PermissionCheck>
  )
}

export default Dashboard
